import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { useLanguage } from '@/context/LanguageContext';
import { devicesAPI, tenantsAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  MagnifyingGlass, ArrowUp, ArrowDown, CaretLeft, CaretRight, Desktop, CircleNotch,
  Eye, Terminal, Play, Lightning, Info, HardDrives, ArrowClockwise, Broom, Wrench,
  WifiHigh, ShieldCheck, FileText, X, CheckSquare, Star, DownloadSimple, UploadSimple,
  Scales, Tag, Lock, LockOpen
} from '@phosphor-icons/react';

const scriptTemplates = [
  { id: 'sysinfo', name: 'scripts.sysinfo', icon: Info, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', script_type: 'powershell', script_content: `$os = Get-CimInstance Win32_OperatingSystem\n$cpu = Get-CimInstance Win32_Processor | Select-Object -First 1\n$uptime = (Get-Date) - $os.LastBootUpTime\nWrite-Output "=== Systeminformationen ==="\nWrite-Output "Hostname: $($env:COMPUTERNAME)"\nWrite-Output "OS: $($os.Caption) $($os.Version)"\nWrite-Output "RAM: $([math]::Round($os.TotalVisibleMemorySize/1MB,1)) GB (Frei: $([math]::Round($os.FreePhysicalMemory/1MB,1)) GB)"\nWrite-Output "CPU: $($cpu.Name)"\nWrite-Output "Uptime: $($uptime.Days)d $($uptime.Hours)h $($uptime.Minutes)m"` },
  { id: 'diskspace', name: 'scripts.diskspace', icon: HardDrives, color: 'text-purple-400 bg-purple-500/10 border-purple-500/20', script_type: 'powershell', script_content: `Get-CimInstance Win32_LogicalDisk -Filter "DriveType=3" | ForEach-Object {\n  $total = [math]::Round($_.Size/1GB,1)\n  $free = [math]::Round($_.FreeSpace/1GB,1)\n  $used = $total - $free\n  $pct = [math]::Round(($used/$total)*100,0)\n  Write-Output "$($_.DeviceID) $used/$total GB belegt ($pct%)"\n}` },
  { id: 'winupdate', name: 'scripts.winupdate', icon: ArrowClockwise, color: 'text-green-400 bg-green-500/10 border-green-500/20', script_type: 'powershell', script_content: `Write-Output "Windows Update wird gestartet..."\nStart-Process -FilePath "UsoClient.exe" -ArgumentList "StartScan" -Wait\nStart-Process -FilePath "UsoClient.exe" -ArgumentList "StartDownload" -Wait\nWrite-Output "Windows Update gestartet."` },
  { id: 'diskcleaner', name: 'scripts.diskcleaner', icon: Broom, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', script_type: 'powershell', script_content: `Write-Output "=== Disk Cleanup ==="\n$before = (Get-CimInstance Win32_LogicalDisk -Filter "DeviceID='C:'").FreeSpace\nRemove-Item "$env:TEMP\\*" -Recurse -Force -ErrorAction SilentlyContinue\n$after = (Get-CimInstance Win32_LogicalDisk -Filter "DeviceID='C:'").FreeSpace\n$freed = [math]::Round(($after - $before)/1MB, 1)\nWrite-Output "Bereinigt: $freed MB freigegeben"` },
  { id: 'services', name: 'scripts.services', icon: Wrench, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', script_type: 'powershell', script_content: `$services = @("WinRM","Spooler","W32Time","wuauserv","BITS","EventLog","Dnscache")\nforeach ($svc in $services) {\n  $s = Get-Service -Name $svc -ErrorAction SilentlyContinue\n  if ($s) { Write-Output "$($s.DisplayName): $($s.Status)" }\n}` },
  { id: 'network', name: 'scripts.network', icon: WifiHigh, color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20', script_type: 'powershell', script_content: `Get-NetIPAddress -AddressFamily IPv4 | Where-Object { $_.InterfaceAlias -notlike "Loopback*" } | ForEach-Object {\n  Write-Output "$($_.InterfaceAlias): $($_.IPAddress)/$($_.PrefixLength)"\n}` },
  { id: 'restart', name: 'scripts.restart', icon: ArrowClockwise, color: 'text-red-400 bg-red-500/10 border-red-500/20', script_type: 'powershell', script_content: `shutdown /r /t 10 /c "TSR Remote Neustart"` },
  { id: 'security', name: 'scripts.security', icon: ShieldCheck, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', script_type: 'powershell', script_content: `$fw = Get-NetFirewallProfile | ForEach-Object { "$($_.Name): $($_.Enabled)" }\nWrite-Output "Firewall: $($fw -join ', ')"` },
  { id: 'eventlog', name: 'scripts.eventlog', icon: FileText, color: 'text-pink-400 bg-pink-500/10 border-pink-500/20', script_type: 'powershell', script_content: `Get-EventLog -LogName System -EntryType Error -Newest 20 | ForEach-Object {\n  Write-Output "[$($_.TimeGenerated.ToString('dd.MM.yyyy HH:mm'))] $($_.Source): $($_.Message.Substring(0, [Math]::Min(120, $_.Message.Length)))..."\n}` },
];

const StatusBadge = ({ isOnline, agentType, t }) => (
  <span className={`status-badge ${isOnline ? 'online' : 'offline'}`}>
    <span className={`status-dot ${isOnline ? 'online' : 'offline'}`} />
    {isOnline ? t('common.online') : t('common.offline')}
    {agentType === 'node' && <span className="agent-v2-badge" title="Node.js Agent v2 (WebSocket)">v2</span>}
  </span>
);

const StatusWithButtons = ({ device, hideStatus = false, t }) => {
  const handleConnect = (e, type) => {
    e.stopPropagation();
    const configs = {
      TV: { id: device.teamviewer_id, url: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? `https://start.teamviewer.com/${device.teamviewer_id}` : `teamviewer10://control?device=${device.teamviewer_id}`, name: 'TeamViewer' },
      RD: { id: device.rustdesk_id, url: `rustdesk://${device.rustdesk_id}`, name: 'RustDesk' },
      AD: { id: device.anydesk_id, url: `anydesk:${device.anydesk_id}`, mobile: true, name: 'AnyDesk' },
    };
    const cfg = configs[type];
    if (!cfg.id) { toast.error(`${cfg.name} ${t('connect.notAvailable')}`); return; }
    if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) && cfg.mobile) {
      navigator.clipboard.writeText(String(cfg.id)).then(() => toast.success(`${cfg.name} ${t('connect.idCopied')} ${cfg.id}`, { duration: 5000 })).catch(() => toast.info(`${cfg.name} ID: ${cfg.id}`, { duration: 5000 }));
      return;
    }
    window.open(cfg.url, '_blank');
    toast.success(`${t('connect.connectingTo')} ${device.computer_name} via ${cfg.name}`);
  };
  return (
    <div className="status-connect-row" data-testid={`status-connect-${device.id}`}>
      {!hideStatus && <StatusBadge isOnline={device.is_online} agentType={device.agent_type} t={t} />}
      <div className={`connect-group ${hideStatus ? 'no-border' : ''}`}>
        {['TV', 'RD', 'AD'].map(type => {
          const idMap = { TV: 'teamviewer_id', RD: 'rustdesk_id', AD: 'anydesk_id' };
          const runMap = { TV: 'tv_running', RD: 'rd_running', AD: 'ad_running' };
          const id = device[idMap[type]];
          const running = device[runMap[type]];
          const isOnline = device.is_online;
          // LED: green = running, orange = has ID but not running or device offline, grey = no ID
          const ledClass = !id ? 'led-inactive' : (!isOnline ? 'led-offline' : (running ? 'led-active' : 'led-warning'));
          const ledTitle = !id ? 'Nicht konfiguriert' : (!isOnline ? 'Geraet offline' : (running ? 'Laeuft' : 'Prozess nicht aktiv'));
          return (
            <div key={type} className="connect-btn-with-led">
              <button onClick={e => handleConnect(e, type)} disabled={!id || !isOnline} className="connect-btn hetzner-red disabled:opacity-50 disabled:cursor-not-allowed">{type}</button>
              <span className={`led-dot-lg ${ledClass}`} title={ledTitle} />
              {id && <span className="text-[10px] font-mono text-[#52525b] hidden xl:inline">{id}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function DevicesPage() {
  const navigate = useNavigate();
  const { isSuperadmin, isAdmin } = useAuth();
  const { subscribe } = useWebSocket();
  const { t } = useLanguage();
  const [devices, setDevices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [runningScript, setRunningScript] = useState(null);
  const [customForm, setCustomForm] = useState({ name: '', script_content: '', script_type: 'powershell' });
  const [pagination, setPagination] = useState({ page: 1, per_page: 25, total: 0, total_pages: 0 });
  const [filters, setFilters] = useState({ search: '', tenant_id: 'all', status: 'all', sort_by: 'computer_name', sort_order: 'asc' });
  const [searchInput, setSearchInput] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareDevices, setCompareDevices] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10);
  const fileInputRef = useRef(null);

  const fetchDevices = useCallback(async () => {
    try {
      const params = { page: pagination.page, per_page: pagination.per_page, sort_by: filters.sort_by, sort_order: filters.sort_order };
      if (filters.search) params.search = filters.search;
      if (filters.tenant_id && filters.tenant_id !== 'all') params.tenant_id = filters.tenant_id;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      const response = await devicesAPI.getAll(params);
      setDevices(response.data.devices);
      setPagination(prev => ({ ...prev, total: response.data.total, total_pages: response.data.total_pages }));
    } catch (error) { toast.error(t('devices.loadError')); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.per_page, filters]);

  const fetchFavorites = useCallback(async () => {
    try { const res = await api.get('/favorites'); setFavorites(res.data.device_ids || []); } catch (e) {}
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/user-settings');
      setAutoRefresh(res.data.auto_refresh !== false);
      setRefreshInterval(res.data.refresh_interval || 10);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);
  useEffect(() => {
    const h = setTimeout(() => { if (filters.search !== searchInput) { setFilters(p => ({ ...p, search: searchInput })); setPagination(p => ({ ...p, page: 1 })); } }, 300);
    return () => clearTimeout(h);
  }, [searchInput, filters.search]);
  useEffect(() => { if (isSuperadmin) { const f = async () => { try { const r = await tenantsAPI.getAll(); setTenants(r.data); } catch (e) {} }; f(); } }, [isSuperadmin]);
  useEffect(() => { fetchFavorites(); fetchSettings(); }, [fetchFavorites, fetchSettings]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchDevices, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchDevices]);

  useEffect(() => {
    const unsubscribe = subscribe((message) => {
      if (message.type === 'device_status') setDevices(prev => prev.map(d => d.id === message.device_id ? { ...d, is_online: message.is_online, last_seen: message.last_seen, ...(message.tv_running !== undefined ? { tv_running: message.tv_running } : {}), ...(message.rd_running !== undefined ? { rd_running: message.rd_running } : {}), ...(message.ad_running !== undefined ? { ad_running: message.ad_running } : {}) } : d));
      else if (['device_new', 'bulk_status_change'].includes(message.type)) fetchDevices();
    });
    return unsubscribe;
  }, [subscribe, fetchDevices]);

  const handleSort = (col) => { setFilters(p => ({ ...p, sort_by: col, sort_order: p.sort_by === col && p.sort_order === 'asc' ? 'desc' : 'asc' })); setPagination(p => ({ ...p, page: 1 })); };
  const handleSearch = (v) => { setFilters(p => ({ ...p, search: v })); setPagination(p => ({ ...p, page: 1 })); };
  const toggleSelect = (e, id) => { e.stopPropagation(); setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); };
  const toggleSelectAll = () => { setSelectedIds(selectedIds.length === devices.length ? [] : devices.map(d => d.id)); };

  const toggleFavorite = async (e, deviceId) => {
    e.stopPropagation();
    if (favorites.includes(deviceId)) {
      await api.delete(`/favorites/${deviceId}`);
      setFavorites(p => p.filter(id => id !== deviceId));
      toast.success(t('favorites.removed'));
    } else {
      await api.post(`/favorites/${deviceId}`);
      setFavorites(p => [...p, deviceId]);
      toast.success(t('favorites.added'));
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/devices/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'tsr-devices-export.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('export.success'));
    } catch (e) { toast.error(t('common.error')); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/devices/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${t('import.success')}: ${res.data.updated} ${t('import.updated')}, ${res.data.total_errors || 0} ${t('import.errors')}`);
      setImportOpen(false);
      fetchDevices();
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCompare = async () => {
    if (selectedIds.length < 2) { toast.error(t('compare.select')); return; }
    try {
      const res = await api.post('/devices/compare', { device_ids: selectedIds.slice(0, 5) });
      setCompareDevices(res.data.devices || []);
      setCompareOpen(true);
    } catch (e) { toast.error(t('common.error')); }
  };

  const toggleAutoRefresh = async () => {
    const newVal = !autoRefresh;
    setAutoRefresh(newVal);
    try { await api.patch('/user-settings', { auto_refresh: newVal, refresh_interval: refreshInterval }); } catch (e) {}
  };

  const executeTemplateOnSelected = async (tpl) => {
    if (selectedIds.length === 0) return;
    setRunningScript(tpl.id);
    try {
      await api.post('/scripts', { name: `${t(tpl.name)} (${selectedIds.length})`, script_content: tpl.script_content, script_type: tpl.script_type, target_type: 'device', target_ids: selectedIds, run_as_admin: true, silent: true });
      toast.success(`"${t(tpl.name)}" ${selectedIds.length} ${t('common.devices')}`);
      setScriptDialogOpen(false); setSelectedIds([]);
    } catch (e) { toast.error(t('scripts.executeError')); }
    finally { setRunningScript(null); }
  };

  const executeCustomOnSelected = async () => {
    if (!customForm.name.trim() || !customForm.script_content.trim()) { toast.error(t('scripts.nameAndScriptRequired')); return; }
    setRunningScript('custom');
    try {
      await api.post('/scripts', { name: `${customForm.name} (${selectedIds.length})`, script_content: customForm.script_content, script_type: customForm.script_type, target_type: 'device', target_ids: selectedIds, run_as_admin: true, silent: true });
      toast.success(t('scripts.scriptRunning'));
      setScriptDialogOpen(false); setSelectedIds([]); setCustomMode(false); setCustomForm({ name: '', script_content: '', script_type: 'powershell' });
    } catch (e) { toast.error(t('scripts.executeError')); }
    finally { setRunningScript(null); }
  };

  const bulkKiosk = async (enable) => {
    if (selectedIds.length === 0) return;
    const label = enable ? 'aktivieren' : 'deaktivieren';
    if (!window.confirm(`Kiosk-Modus fuer ${selectedIds.length} Geraete ${label}?\n(Wird nach Neustart wirksam)`)) return;
    setRunningScript(enable ? 'kiosk-on' : 'kiosk-off');
    try {
      const res = await api.post('/agent/kiosk-bulk', { device_ids: selectedIds, enable });
      toast.success(res.data?.message || `Kiosk ${label} fuer ${res.data?.dispatched_count || 0} Geraete`);
      setSelectedIds([]);
      fetchDevices();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk-Aktion fehlgeschlagen'); }
    finally { setRunningScript(null); }
  };

  const SortIcon = ({ column }) => { if (filters.sort_by !== column) return null; return filters.sort_order === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />; };
  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return '-';
    const dm = Math.floor((new Date() - new Date(lastSeen)) / 60000);
    if (dm < 1) return t('devices.justNow');
    if (dm < 60) return `${dm}m`;
    if (dm < 1440) return `${Math.floor(dm / 60)}h`;
    return new Date(lastSeen).toLocaleDateString('de-DE');
  };

  return (
    <div data-testid="devices-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{t('devices.title')}</h1>
          <p className="text-[#a1a1aa] text-sm mt-1">{pagination.total} {t('common.devices')} {t('devices.registered')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={toggleAutoRefresh} data-testid="auto-refresh-toggle"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium border transition-colors ${autoRefresh ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-[#0A0A0B] text-[#52525b] border-white/10'}`}
            title={t('autoRefresh.label')}>
            <ArrowClockwise size={12} className={autoRefresh ? 'animate-spin' : ''} style={autoRefresh ? { animationDuration: `${refreshInterval}s` } : {}} />
            {refreshInterval}s
          </button>
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" onClick={handleExport} className="border-white/10 h-8 text-xs" data-testid="csv-export-btn">
                <DownloadSimple size={14} /> {t('devices.export')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} className="border-white/10 h-8 text-xs" data-testid="csv-import-btn">
                <UploadSimple size={14} /> {t('devices.import')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 mb-6 p-3 sm:p-4 bg-[#141416] border border-white/10 rounded">
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[240px] relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
          <Input placeholder={t('common.search') + ' (Hostname, Stadt, TV/RD/AD-ID, IP, Station-Code)'} value={searchInput} onChange={e => setSearchInput(e.target.value)} data-testid="search-input"
            className="pl-10 bg-[#0A0A0B] border-white/10 text-white placeholder:text-[#52525b]" />
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          {isSuperadmin && (
            <Select value={filters.tenant_id} onValueChange={v => { setFilters(p => ({ ...p, tenant_id: v })); setPagination(p => ({ ...p, page: 1 })); }}>
              <SelectTrigger className="flex-1 sm:w-[180px] bg-[#0A0A0B] border-white/10"><SelectValue placeholder={t('devices.allTenants')} /></SelectTrigger>
              <SelectContent className="bg-[#141416] border-white/10"><SelectItem value="all">{t('devices.allTenants')}</SelectItem>{tenants.map(tn => <SelectItem key={tn.id} value={tn.id}>{tn.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={filters.status} onValueChange={v => { setFilters(p => ({ ...p, status: v })); setPagination(p => ({ ...p, page: 1 })); }}>
            <SelectTrigger className="w-[140px] bg-[#0A0A0B] border-white/10"><SelectValue placeholder={t('devices.allStatuses')} /></SelectTrigger>
            <SelectContent className="bg-[#141416] border-white/10"><SelectItem value="all">{t('devices.allStatuses')}</SelectItem><SelectItem value="online">{t('common.online')}</SelectItem><SelectItem value="offline">{t('common.offline')}</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-[#D50C2D]" /></div>
      ) : devices.length === 0 ? (
        <div className="text-center py-16 bg-[#141416] border border-white/10 rounded"><Desktop size={48} className="mx-auto text-[#52525b] mb-4" /><p className="text-[#a1a1aa]">{t('devices.noDevices')}</p></div>
      ) : (
        <>
          <div className="hidden md:block bg-[#141416] border border-white/10 rounded overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="devices-table">
                <thead><tr>
                  <th className="w-10"><div onClick={toggleSelectAll} className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer mx-auto ${selectedIds.length === devices.length && devices.length > 0 ? 'bg-[#D50C2D] border-[#D50C2D]' : 'border-white/20 hover:border-white/40'}`}>{selectedIds.length === devices.length && devices.length > 0 && <CheckSquare size={12} weight="bold" className="text-white" />}</div></th>
                  <th className="w-10"></th>
                  <th className="cursor-pointer" onClick={() => handleSort('computer_name')}><div className="flex items-center gap-2">{t('devices.computerName')} <SortIcon column="computer_name" /></div></th>
                  <th>{t('devices.statusConnect')}</th>
                  <th className="w-20 text-center" title="Kiosk-Modus">Kiosk</th>
                  {isSuperadmin && <th>{t('devices.tenant')}</th>}
                  <th className="cursor-pointer" onClick={() => handleSort('location_city')}><div className="flex items-center gap-2">{t('devices.location')} <SortIcon column="location_city" /></div></th>
                  <th className="cursor-pointer" onClick={() => handleSort('last_seen')}><div className="flex items-center gap-2">{t('devices.lastSeen')} <SortIcon column="last_seen" /></div></th>
                  <th></th></tr>
                </thead>
                <tbody>
                  {devices.map(device => (
                    <tr key={device.id} className={`table-row-hover cursor-pointer ${selectedIds.includes(device.id) ? 'bg-[#D50C2D]/5' : ''}`}
                      data-testid={`device-row-${device.id}`} onClick={() => navigate(`/devices/${device.id}`)}>
                      <td onClick={e => toggleSelect(e, device.id)}>
                        <div className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer mx-auto ${selectedIds.includes(device.id) ? 'bg-[#D50C2D] border-[#D50C2D]' : 'border-white/20 hover:border-white/40'}`}>
                          {selectedIds.includes(device.id) && <CheckSquare size={12} weight="bold" className="text-white" />}
                        </div>
                      </td>
                      <td onClick={e => toggleFavorite(e, device.id)}>
                        <Star size={16} weight={favorites.includes(device.id) ? 'fill' : 'regular'}
                          className={`cursor-pointer transition-colors ${favorites.includes(device.id) ? 'text-yellow-400' : 'text-[#3f3f46] hover:text-yellow-400'}`} />
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          <Desktop size={18} className="text-[#52525b]" />
                          <div>
                            <span className="font-medium text-white">{device.computer_name}</span>
                            {device.tags?.length > 0 && (
                              <div className="flex gap-1 mt-0.5">{device.tags.map(tag => <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{tag}</span>)}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td><StatusWithButtons device={device} t={t} /></td>
                      <td className="text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${device.kiosk_mode ? 'bg-green-600/10 text-green-400 border-green-500/30' : 'bg-zinc-700/20 text-zinc-500 border-zinc-600/30'}`} data-testid={`kiosk-badge-${device.id}`}>
                          {device.kiosk_mode ? <><Lock size={10} weight="bold" /> EIN</> : <><LockOpen size={10} /> AUS</>}
                        </span>
                      </td>
                      {isSuperadmin && <td><span className="tenant-badge">{device.tenant_code}</span></td>}
                      <td className="text-[#a1a1aa]">{device.location_city || '-'}{device.station_code && <span className="text-[#52525b] ml-1">({device.station_code})</span>}</td>
                      <td className="font-mono text-xs text-[#52525b]">{formatLastSeen(device.last_seen)}</td>
                      <td onClick={e => e.stopPropagation()}><button onClick={() => navigate(`/devices/${device.id}`)} className="p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded transition-colors inline-flex"><Eye size={18} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
                <p className="text-[#52525b] text-sm">{t('devices.page')} {pagination.page} {t('devices.of')} {pagination.total_pages}</p>
                <div className="pagination">
                  <button onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="pagination-btn"><CaretLeft size={16} /></button>
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pn; if (pagination.total_pages <= 5) pn = i + 1; else if (pagination.page <= 3) pn = i + 1; else if (pagination.page >= pagination.total_pages - 2) pn = pagination.total_pages - 4 + i; else pn = pagination.page - 2 + i;
                    return <button key={pn} onClick={() => setPagination(p => ({ ...p, page: pn }))} className={`pagination-btn ${pagination.page === pn ? 'active' : ''}`}>{pn}</button>;
                  })}
                  <button onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.total_pages} className="pagination-btn"><CaretRight size={16} /></button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {devices.map(device => (
              <div key={device.id} className="device-card-mobile" data-testid={`device-row-${device.id}`}>
                <div className="card-header" onClick={() => navigate(`/devices/${device.id}`)}>
                  <div className="flex items-center gap-2">
                    <Star size={14} weight={favorites.includes(device.id) ? 'fill' : 'regular'} onClick={e => toggleFavorite(e, device.id)}
                      className={favorites.includes(device.id) ? 'text-yellow-400' : 'text-[#3f3f46]'} />
                    <span className="card-name">{device.computer_name}</span>
                  </div>
                  <StatusBadge isOnline={device.is_online} agentType={device.agent_type} t={t} />
                </div>
                <div className="card-meta">
                  {isSuperadmin && <span className="tenant-badge">{device.tenant_code}</span>}
                  {device.location_city && <span>{device.location_city}</span>}
                </div>
                <div className="card-actions" onClick={e => e.stopPropagation()}><StatusWithButtons device={device} hideStatus t={t} /></div>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#141416] border border-white/10 rounded-xl shadow-2xl shadow-black/50 px-5 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-4" data-testid="selection-action-bar">
          <span className="text-white text-sm font-medium">{selectedIds.length} {t('devices.selected')}</span>
          <div className="w-px h-6 bg-white/10" />
          <Button size="sm" onClick={() => { setScriptDialogOpen(true); setCustomMode(false); }} className="bg-[#D50C2D] hover:bg-[#f21d41]"><Terminal size={14} /> {t('devices.executeScript')}</Button>
          {selectedIds.length >= 2 && <Button size="sm" variant="outline" onClick={handleCompare} className="border-white/10 h-8"><Scales size={14} /> {t('devices.compare')}</Button>}
          <div className="w-px h-6 bg-white/10" />
          <Button size="sm" variant="outline" onClick={() => bulkKiosk(true)} disabled={runningScript === 'kiosk-on'} className="border-green-500/30 text-green-400 hover:bg-green-600/10 hover:text-green-300 h-8" data-testid="bulk-kiosk-on-btn">
            {runningScript === 'kiosk-on' ? <CircleNotch size={14} className="animate-spin" /> : <Lock size={14} weight="bold" />} Kiosk EIN
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulkKiosk(false)} disabled={runningScript === 'kiosk-off'} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700/30 h-8" data-testid="bulk-kiosk-off-btn">
            {runningScript === 'kiosk-off' ? <CircleNotch size={14} className="animate-spin" /> : <LockOpen size={14} />} Kiosk AUS
          </Button>
          <button onClick={() => setSelectedIds([])} className="p-1.5 text-[#52525b] hover:text-white rounded"><X size={16} /></button>
        </div>
      )}

      {/* Script Dialog */}
      <Dialog open={scriptDialogOpen} onOpenChange={setScriptDialogOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{t('devices.executeScript')} ({selectedIds.length} {t('common.devices')})</DialogTitle></DialogHeader>
          {!customMode ? (
            <div className="py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {scriptTemplates.map(tpl => (
                  <button key={tpl.id} onClick={() => executeTemplateOnSelected(tpl)} disabled={runningScript === tpl.id}
                    className="flex items-center gap-3 p-3 bg-[#0A0A0B] rounded border border-white/5 hover:border-white/15 transition-colors text-left group disabled:opacity-60">
                    <div className={`p-1.5 rounded border shrink-0 ${tpl.color}`}>{runningScript === tpl.id ? <CircleNotch size={16} className="animate-spin" /> : <tpl.icon size={16} />}</div>
                    <span className="text-white text-sm font-medium flex-1 truncate">{t(tpl.name)}</span>
                    <Play size={14} className="text-[#52525b] group-hover:text-[#D50C2D] transition-colors shrink-0" />
                  </button>
                ))}
              </div>
              <div className="border-t border-white/5 pt-3"><Button size="sm" variant="outline" onClick={() => setCustomMode(true)} className="border-white/10 w-full"><Terminal size={14} /> {t('devices.customScript')}</Button></div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[#a1a1aa]">{t('common.name')}</Label><Input value={customForm.name} onChange={e => setCustomForm(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" /></div>
                <div><Label className="text-[#a1a1aa]">{t('common.type')}</Label>
                  <Select value={customForm.script_type} onValueChange={v => setCustomForm(p => ({...p, script_type: v}))}>
                    <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141416] border-white/10"><SelectItem value="powershell">PowerShell</SelectItem><SelectItem value="cmd">CMD</SelectItem><SelectItem value="batch">Batch</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-[#a1a1aa]">Skript</Label><textarea value={customForm.script_content} onChange={e => setCustomForm(p => ({...p, script_content: e.target.value}))} className="w-full mt-1 p-3 bg-[#0A0A0B] border border-white/10 rounded text-sm text-white font-mono resize-y min-h-[120px] focus:outline-none focus:border-[#D50C2D]" /></div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCustomMode(false)} className="border-white/10">{t('common.back')}</Button>
                <Button onClick={executeCustomOnSelected} disabled={runningScript === 'custom'} className="bg-[#D50C2D] hover:bg-[#f21d41]">{runningScript === 'custom' ? <CircleNotch size={16} className="animate-spin" /> : <><Play size={16} /> {t('common.execute')}</>}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{t('import.title')}</DialogTitle></DialogHeader>
          <p className="text-[#a1a1aa] text-sm">{t('import.desc')}</p>
          <div className="bg-[#0A0A0B] rounded p-3 border border-white/5 text-[#52525b] text-xs font-mono">
            <p className="text-[#a1a1aa] mb-1">CSV Format (Semikolon-getrennt):</p>
            <p>computer_name;location_city;location_country;location_state;station_code;poi_type;notes</p>
          </div>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImport} className="block w-full text-sm text-[#a1a1aa] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-[#D50C2D] file:text-white hover:file:bg-[#f21d41] cursor-pointer" data-testid="csv-file-input" />
        </DialogContent>
      </Dialog>

      {/* Compare Dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{t('compare.title')} ({compareDevices.length})</DialogTitle></DialogHeader>
          {compareDevices.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/10">
                  <th className="text-left text-[#52525b] py-2 px-3 text-xs uppercase">Eigenschaft</th>
                  {compareDevices.map(d => <th key={d.id} className="text-left text-white py-2 px-3 font-medium">{d.computer_name}</th>)}
                </tr></thead>
                <tbody>
                  {[
                    ['Status', d => d.is_online ? '🟢 Online' : '🔴 Offline'],
                    ['Mandant', d => d.tenant_code || '-'],
                    ['TeamViewer', d => d.teamviewer_id || '-'],
                    ['RustDesk', d => d.rustdesk_id || '-'],
                    ['AnyDesk', d => d.anydesk_id || '-'],
                    ['IP', d => d.ip_address || '-'],
                    ['OS', d => d.os_info || '-'],
                    ['Stadt', d => d.location_city || '-'],
                    ['Land', d => d.location_country || '-'],
                    ['Station', d => d.station_code || '-'],
                    ['POI', d => d.poi_type || '-'],
                    ['Tags', d => d.tags?.join(', ') || '-'],
                    ['Zuletzt gesehen', d => d.last_seen ? new Date(d.last_seen).toLocaleString('de-DE') : '-'],
                  ].map(([label, fn]) => (
                    <tr key={label} className="border-b border-white/5">
                      <td className="text-[#52525b] py-2 px-3 text-xs">{label}</td>
                      {compareDevices.map(d => <td key={d.id} className="text-[#a1a1aa] py-2 px-3 font-mono text-xs">{fn(d)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
